/**
 * 対象の配列内の各要素に対して値取得関数を実行し、比較関数に基づいて要素を選択する。
 *
 * @param source 対象の配列
 * @param valueFunction 値取得関数
 * @param comparator 比較関数(trueを返す方が選択される)
 * @returns 条件を満たす要素
 */
const selectBy = <T, V extends number | string | Date>(source: readonly T[], valueFunction: (item: T) => V, comparator: (current: V, target: V) => boolean) => {
    if (source.length === 0) {
        throw new Error("source is empty.");
    }

    let selectedItem = source[0];
    let selectedValue = valueFunction(selectedItem);

    for (let i = 1; i < source.length; i++) {
        const currentValue = valueFunction(source[i]);
        if (comparator(currentValue, selectedValue)) {
            selectedValue = currentValue;
            selectedItem = source[i];
        }
    }

    return selectedItem;
};

/**
 * 対象の配列内の各要素に対して値取得関数を実行し、その値が一番小さいものを返す。
 * 同一の値となる要素が複数あった場合、先頭に近い要素が選択される。
 *
 * @param source 対象の配列
 * @param valueFunction 値取得関数
 * @returns 条件を満たす要素
 */
export const minBy = <T, V extends number | string | Date>(source: readonly T[], valueFunction: (item: T) => V) => {
    return selectBy(source, valueFunction, (current, target) => current < target);
};

/**
 * 対象の配列内の各要素に対して値取得関数を実行し、その値が一番大きいものを返す。
 * 同一の値となる要素が複数あった場合、先頭に近い要素が選択される。
 *
 * @param source 対象の配列
 * @param valueFunction 値取得関数
 * @returns 条件を満たす要素
 */
export const maxBy = <T, V extends number | string | Date>(source: readonly T[], valueFunction: (item: T) => V) => {
    return selectBy(source, valueFunction, (current, target) => current > target);
};
